import { IMAGES } from "../assets";

// COMPONENTS
import { LATextInput, LAButton, LAView, LAText } from "../components";

// PACKAGES
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

// CONSTANTS & LIBRARIES
import { STRINGS } from "../constant";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import {
  SIGN_IN_FIREBASE,
  INIT_FIREBASE_APP,
  SIGN_IN_GOOGLE,
  SIGN_UP_APPLE,
} from "../api/firebase_auth";
import { SHOW_TOAST, detectOS } from "../constant/utils";
import { useContext, useEffect, useState } from "react";
import { getAppLogo } from "../api/theme_settings";
import { ThemeContext } from "../context/ThemeProvider";
import { MixpanelContext } from "../context/MixpanelProvider";
import useFeatureList from "../hooks/useFeatureList";
import theme_config from "../../config";
import { getClientId } from "../api/client";
import { createLogger } from "../utils/logger";

const gradientAnimationStyle = `
  @keyframes bgShift {
    0%   { background-position: 0% 50%; }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  .login-bg {
    animation: bgShift 120s ease infinite;
    background-size: 200% 200%;
  }
`;
const styleEl = document.createElement("style");
styleEl.textContent = gradientAnimationStyle;
document.head.appendChild(styleEl);

function Login() {
  const [loginState, setLoginState] = useState({ isLoading: false });
  const [showPassword, setShowPassword] = useState(false);
  const [logo, setLogo] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const navigation = useNavigate();
  const { trackOnboardingSingup } = useContext(MixpanelContext);
  const { theme, currentTheme, setCurrentTheme } = useContext(ThemeContext);
  const isDark = currentTheme === "dark";
  const featureList = useFeatureList();
  const logger = createLogger(process.env.REACT_APP_ENV === 'DEVELOPMENT', 'Login');

  // ---- theme vars with fallbacks ----
  const bgStart =
    theme?.background?.gradientStart ||
    (isDark ? "hsl(210,30%,8%)" : "hsl(210,10%,95%)");
  const bgEnd =
    theme?.background?.gradientEnd ||
    (isDark ? "hsl(210,30%,12%)" : "hsl(210,10%,90%)");    

  const glassBg = isDark
    ? theme?.card?.light
    : theme?.card?.dark;
  const glassBorder = isDark
    ? theme?.border?.dark
    : theme?.border?.light;
  const txt1 = theme?.text?.login_primary || (isDark ? "#fff" : "#111");
  const txt2 = theme?.text?.login_secondary || (isDark ? "rgba(255,255,255,0.75)" : "#555");
  const muted = theme?.text?.muted || (isDark ? "#888" : "#999");
  const linkCol = theme?.text?.link || "#0066cc";
  const inpBd = theme?.input?.border || (isDark ? "#555" : "#ddd");
  const errCol = theme?.status?.error || "#dc3545";
  const focusRing = theme?.primary?.main || "#0066cc";
  const btnBg = theme?.button?.primary?.background || "#0066cc";
  const btnText = theme?.button?.primary?.text || "#fff";
  const radius = "1rem";

  // ---- init & fetch theme/logo ----
  useEffect(() => { INIT_FIREBASE_APP(); }, []);
  useEffect(() => { getAppLogo(currentTheme).then(setLogo); }, []);

  // Initialize client configuration (similar to activation screen)
  useEffect(() => {
    const initializeClient = async () => {
      try {
        logger.log("Initializing client configuration...");
        const id = await getClientId();
        logger.log("Client ID obtained:", id);
        setClientId(id);
        setIsInitializing(false);
      } catch (error) {
        logger.error("Error initializing client:", error);
        // Continue with login even if client initialization fails
        setIsInitializing(false);
      }
    };

    initializeClient();
  }, []);

  useEffect(() => {
    const fetchDefaultTheme = async () => {
      const mode = theme_config.default_theme_mode;
  
      if (mode) {
        const root = document.documentElement;

        if (mode === "dark") {
          root.classList.add("dark");
          root.classList.remove("light");
        } else if (mode === "light") {
          root.classList.add("light");
          root.classList.remove("dark");
        }

        setCurrentTheme(mode);
      }
    };
  
    fetchDefaultTheme();
  }, []);

  // ---- form handling ----
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Please enter your email")
      .matches(/^\S+@\S+\.\S+$/, "Invalid email"),
    password: Yup.string()
      .required("Please enter your password")
      .min(6, "At least 6 chars")
      .matches(/^\S*$/, "No spaces allowed"),
  });

  const handleLogin = async (values, { setFieldValue }) => {
    trackOnboardingSingup("Signup");
    setLoginState({ isLoading: true });
    if (!values.email || !values.password) {
      SHOW_TOAST("Email & password required", "error");
      return setLoginState({ isLoading: false });
    }
    const res = await SIGN_IN_FIREBASE(values.email, values.password);
    setLoginState({ isLoading: false });
    if (res.status) {
      navigation("/prepare", { state: { /* ... */ } });
    } else {
      SHOW_TOAST(res.error, "error");
      setFieldValue("password", "");
    }
  };
  // ——— Social sign‑in handlers ———
  const signInWithGoogleHandler = async () => {
    trackOnboardingSingup("Google");
    setLoginState({ isLoading: true });
    const result = await SIGN_IN_GOOGLE();
    setLoginState({ isLoading: false });
    if (result.status) {
      navigation("/prepare", {
        state: { /* same payload you use in handleLogin */ }
      });
    } else if (result.error) {
      SHOW_TOAST(result.error, "error", 5000);
    }
  };

  const signInWithAppleHandler = async () => {
    trackOnboardingSingup("Apple");
    setLoginState({ isLoading: true });
    const result = await SIGN_UP_APPLE();
    setLoginState({ isLoading: false });
    if (result.status) {
      navigation("/prepare", {
        state: { /* same payload you use in handleLogin */ }
      });
    } else if (result.error) {
      SHOW_TOAST(result.error, "error", 5000);
    }
  };

  // Show loading state while initializing
  if (isInitializing) {
    return (
      <div
        className="relative min-h-screen flex items-center justify-center px-4 login-bg"
        style={{
          background: `linear-gradient(135deg, ${bgStart}, ${bgEnd})`,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p style={{ color: txt1 }}>Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen flex items-center justify-center px-4 login-bg"
      style={{
        background: `linear-gradient(135deg, ${bgStart}, ${bgEnd})`,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <div
        className="w-full max-w-lg p-10 space-y-20 transition-opacity transform"
        style={{
          background: glassBg,
          backdropFilter: "blur(12px)",
          border: `0px solid ${glassBorder}`,
          borderRadius: radius,
        }}
      >
        {/* logo & header */}
        <div className="text-center space-y-2">
          <img
            src={logo ?? IMAGES.logo}
            alt="Logo"
            className="mx-auto h-12 mb-10"
          />
          <h1
            className="text-3xl font-semibold"
            style={{ color: txt1, fontFamily: "'Poppins', sans-serif" }}
          >
            Welcome Back!
          </h1>
          <p className="italic text-sm" style={{ color: txt2 }}>
            Sign in to continue your learning journey
          </p>
        </div>

        {/* form */}
        <Formik
          initialValues={{ email: "", password: "" }}
          onSubmit={handleLogin}
          validationSchema={validationSchema}
        >
          {({
            values,
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit} className="space-y-8">
              {[
                {
                  name: "email",
                  type: "text",
                  icon: faEnvelope,
                  placeholder: "Email address",
                },
                {
                  name: "password",
                  type: showPassword ? "text" : "password",
                  icon: faLock,
                  placeholder: "Password",
                },
              ].map(({ name, type, icon, placeholder }) => (
                <div key={name} className="relative">
                  <FontAwesomeIcon
                    icon={icon}
                    className="absolute left-0 top-1/2 -translate-y-1/2 text-lg pl-4 pr-1"
                    style={{ color: txt2, opacity: 0.6 }}
                  />
                  <input
                    name={name}
                    type={type}
                    value={values[name]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    className="w-full pl-12 py-3 bg-transparent placeholder-opacity-60 focus:outline-none"
                    style={{
                      border: "none",
                      borderBottom: `1px solid ${errors[name] ? errCol : inpBd}`,
                      caretColor: focusRing,
                      color: txt1,
                    }}
                  />
                  {name === "password" && (
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute right-0 top-1/2 -translate-y-1/2 p-2"
                      style={{ color: txt2 }}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEye : faEyeSlash}
                      />
                    </button>
                  )}
                  <ErrorMessage
                    name={name}
                    component="div"
                    className="mt-1 text-xs"
                    style={{ color: errCol }}
                  />
                </div>
              ))}

              <p
                onClick={() => navigation("/forgot_password")}
                className="text-right text-sm cursor-pointer hover:underline"
                style={{ color: linkCol }}
              >
                {STRINGS.forgot_password}
              </p>

              <div className="pt-10">
                <div className="flex justify-center">
                  <button
                    type="submit"
                    disabled={loginState.isLoading}
                    className="w-3/4 py-5 font-semibold uppercase rounded-full transition-transform active:scale-95 cursor-pointer"
                    style={{
                      backgroundColor: btnBg,
                      color: btnText,
                      letterSpacing: "1px",
                    }}
                  >
                    {STRINGS.signin_register}
                  </button>
                </div>
              </div>

              {/* social */}
              {featureList?.signin.social_sign_in && (
                <div className="flex justify-center mt-10">
                  <div className="flex space-x-3 w-3/4">
                    <button
                      type="button"
                      onClick={signInWithGoogleHandler}
                      className="flex-1 flex items-center justify-center py-3 border rounded-full transition-transform active:scale-95"
                      style={{
                        background: "transparent",
                        borderColor: inpBd,
                        color: txt1,
                      }}
                    >
                      <i className="fab fa-google mr-2" />
                      {STRINGS.google}
                    </button>
                    <button
                      type="button"
                      onClick={signInWithAppleHandler}
                      className="flex-1 flex items-center justify-center py-3 border rounded-full transition-transform active:scale-95"
                      style={{
                        background: "transparent",
                        borderColor: inpBd,
                        color: txt1,
                      }}
                    >
                      <i className="fab fa-apple mr-2" />
                      {STRINGS.apple}
                    </button>
                  </div>
                </div>
              )}
            </form>
          )}
        </Formik>

        {/* signup link */}
        {featureList?.signup.email && (
          <div className="pt-4 text-center">
            <div
              className="h-px mb-4"
              style={{ backgroundColor: inpBd }}
            />
            <p className="text-sm" style={{ color: muted }}>
              {STRINGS.dont_have_an_account}{" "}
              <span
                onClick={() => navigation("/signup/activation")}
                className="font-medium cursor-pointer hover:underline mt-10"
                style={{ color: linkCol }}
              >
                Activate Account
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
